import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useReducedMotion, useScroll } from 'framer-motion';
import { ArrowRightIcon, VideoIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { useOverlayActions } from '../../contexts/StoreContext';
import { founder } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { ease } from '../../core/format/motion';
import { mediaOr } from '../../core/rules/media';
import type { Media } from '../../core/types/media';
import { fill } from '../../utils/copy';
import { eyebrowCls, quietLink, sectionHeading } from '../../ui/sections';

/**
 * "Eerst je huid, dan pas producten": de werkwijze als ritueel, op salie. De scène van de pagina.
 *
 * - De sectie schuift als een kaart óver de vorige heen (ronde bovenhoeken, zachte schaduw).
 * - Computer: links blijft het kader staan (sticky). Bij een nieuwe stap opent de nieuwe foto als
 *   een gordijn van onderen; terug scrollen sluit hem. Rechts scrol je door Kijken, Kiezen,
 *   Opbouwen: de actieve stap staat helder, een dunne lijn ernaast vult zich mee.
 * - Telefoon: stapelkaarten. Elke stap blijft bovenin staan tot de volgende eroverheen schuift;
 *   de kopregels van de vorige blijven zichtbaar.
 *
 * Beelden per stap uit Paginateksten (home.story.steps[].media); leeg = het salonbeeld, het
 * portret met producten, het portret buiten. Shell op salie 5,4:1; gedimde koppen ≥ 3:1 (grote tekst).
 */
export function HomeStory() {
  const pages = usePages();
  const {
    policy
  } = useSite();
  const {
    openBooking
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const copy = pages.home.story;
  const [active, setActive] = useState(0);
  const stepsRef = useRef<HTMLOListElement>(null);
  const {
    scrollYProgress: stepsProgress
  } = useScroll({
    target: stepsRef,
    offset: ['start center', 'end center']
  });
  const fallbacks: Media[] = [mediaOr(copy.media, {
    image: founder.atWork,
    alt: `${founder.name} tijdens een behandeling in de salon`
  }), {
    image: founder.withProducts,
    alt: `${founder.name} met de producten die ze kiest`,
    focus: {
      x: 50,
      y: 35
    }
  }, {
    image: founder.portraitPalm,
    alt: founder.name,
    focus: {
      x: 50,
      y: 30
    }
  }];
  const imageFor = (i: number): Media => mediaOr(copy.steps[i]?.media ?? {
    image: '',
    alt: ''
  }, fallbacks[i % fallbacks.length]);
  return <section className="relative z-raised -mt-8 rounded-t-[2rem] bg-sage text-shell shadow-[0_-24px_48px_-28px_rgba(36,31,28,0.35)] lg:-mt-14 lg:rounded-t-[3rem]" aria-labelledby="home-story" data-id="element-7515" data-mp="4xw6hyxu2q5o">
      <div className="mx-auto max-w-content px-6 pt-20 sm:pt-24 lg:px-10 lg:pt-32" data-id="element-7516" data-mp="3m8dso51njlc2">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-16" data-id="element-7517" data-mp="3maqh911x0ty3">
          <div className="lg:col-span-7" data-id="element-7518" data-mp="1pessxbpcaw25">
            {copy.eyebrow && <p className={`${eyebrowCls} text-shell`} data-id="element-7519" data-mp="1kh93rgr3syvf">{fill(copy.eyebrow)}</p>}
            <h2 id="home-story" className={`mt-5 ${sectionHeading}`} data-id="element-7520" data-mp="1kf69ulmtyllf">
              <HeadingText heading={copy.heading} accentBlock accentClassName="text-blush" data-id="element-7521" data-mp="1sxrioph4s5tv" />
            </h2>
          </div>
          {copy.body && <p className="max-w-md text-[17px] leading-relaxed text-shell lg:col-span-5 lg:self-end" data-id="element-7522" data-mp="21qbp4c3nzep9">{fill(copy.body)}</p>}
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-12 px-6 pb-20 pt-12 sm:pb-24 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pb-32 lg:pt-16" data-id="element-7523" data-mp="26o3eozgu6oqa">
        {copy.steps.length > 0 && <div className="hidden lg:col-span-6 lg:block" data-id="element-7524" data-mp="1dzz5w52yxvjd">
            <div className="sticky top-[104px] h-[calc(100svh-140px)] max-h-[780px] overflow-hidden bg-clay" data-id="element-7525" data-mp="1e2k9ozmtuljr">
              {/*
                Het kader staat stil; alleen de foto erin wisselt. De nieuwe opent als een gordijn
                van onderen (de foto zelf blijft staan en valt zacht op zijn plek), terug scrollen
                sluit hem weer. Eén beweging per wissel: geen meedrijvend beeld, geen tweede teller
                (de lijn naast de stappen zegt al waar je bent).
               */}
              {copy.steps.map((s, i) => {
            const shown = i <= active;
            return <motion.div key={`${i}-${s.title}`} className="absolute inset-0" initial={false} animate={{
              clipPath: shown ? 'inset(0% 0% 0% 0%)' : 'inset(100% 0% 0% 0%)'
            }} transition={{
              duration: reduce || i === 0 ? 0 : 0.3,
              ease
            }} aria-hidden={i !== active} data-id="element-7526" data-mp="l62r2stgcu19" data-mpi={i}>
                    <motion.div className="h-full w-full" initial={false} animate={{
                scale: reduce || i === active ? 1 : 1.05
              }} transition={{
                duration: reduce ? 0 : 0.3,
                ease
              }} data-id="element-7527" data-mp="13xbx2s40l0o3">
                      <MediaView media={imageFor(i)} sizes="45vw" className="h-full w-full" data-id="element-7528" data-mp="18w5euwqc3du2" />
                    </motion.div>
                  </motion.div>;
          })}
            </div>
          </div>}

        <div className={copy.steps.length > 0 ? 'lg:col-span-6' : 'lg:col-span-12'} data-id="element-7529" data-mp="1xfqn95eecf5j">
          {copy.steps.length > 0 && <ol ref={stepsRef} className="relative lg:pl-10" data-id="element-7530" data-mp="1ij9cafa4d2ji">
              {/* De voortgangslijn naast de stappen (computer). Bij "minder beweging" staat hij vol. */}
              <span aria-hidden="true" className="absolute inset-y-0 left-0 hidden w-px bg-shell/20 lg:block" data-id="element-7531" data-mp="19q61yfurt5t3">
                <motion.span style={reduce ? undefined : {
              scaleY: stepsProgress
            }} className="absolute inset-0 origin-top bg-shell" data-id="element-7532" data-mp="1eiperalmvwwu" />
              </span>
              {copy.steps.map((s, i) => <Step key={`${i}-${s.title}`} index={i} isActive={i === active} title={fill(s.title)} body={fill(s.body)} media={imageFor(i)} onActive={setActive} data-id="element-7533" data-mp="14sw54f51xrv8" data-mpi={i} />)}
            </ol>}

          {(copy.cta || copy.secondaryCta) && <div className="flex flex-col gap-3 border-t border-shell/25 pt-10 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6" data-id="element-7534" data-mp="2sp3q4d6dmx7v">
              {copy.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-shell px-7 text-[15px] text-ink transition-colors duration-150 ease-soft hover:bg-blush" data-id="element-7535" data-mp="2is7ngo35t5b8">
                  {fill(copy.cta)}
                </button>}
              {copy.secondaryCta && <Link to="/behandelingen" className={`${quietLink} text-shell`} data-id="element-7536" data-mp="32hrlr4lmev50">
                  {fill(copy.secondaryCta)}
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7537" data-mp="1r0rpxz02dy68" />
                </Link>}
            </div>}
          {copy.consultLine && <button type="button" onClick={() => openBooking(policy.consult.slug)} aria-haspopup="dialog" className="mt-6 inline-flex min-h-[44px] items-start gap-3 text-left text-[15px] leading-relaxed text-shell underline-offset-4 hover:underline" data-id="element-7538" data-mp="ak2kftyxgqfs">
              <VideoIcon className="mt-1 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-7539" data-mp="p9rxamgduivw" />
              {fill(copy.consultLine)}
            </button>}
        </div>
      </div>
    </section>;
}

/**
 * Stapelkaarten op de telefoon: afstand tot de bovenrand, en hoeveel van elke vorige kaart
 * zichtbaar blijft. Precies de kopregel (cijfer + titel, h-14), zodat bovenin altijd leesbaar
 * staat welke stappen je al had. Een kaart past met drie kopregels erboven nog op een telefoon.
 */
const STACK_TOP_PX = 12;
const STACK_PEEK_PX = 56;
type StepProps = {
  index: number;
  isActive: boolean;
  title: string;
  body: string;
  media: Media;
  onActive: (i: number) => void;
};
function Step({
  index,
  isActive,
  title,
  body,
  media,
  onActive
}: StepProps) {
  const ref = useRef<HTMLLIElement>(null);
  // "Actief" zodra de stap de middenband van het scherm kruist.
  const inView = useInView(ref, {
    margin: '-45% 0px -45% 0px'
  });
  useEffect(() => {
    if (inView) onActive(index);
  }, [inView, index, onActive]);
  return (
    // Telefoon: de stappen zijn kaarten die op elkaar stapelen. Elke kaart blijft bovenin staan tot
    // de volgende eroverheen schuift; de kopregel van de vorige blijft zichtbaar, zodat je ziet
    // waar je in de volgorde bent. Geen extra beweging in de kaart zelf: het stapelen ís de beweging.
    // Computer: een gewone rij naast het vaste beeld.
    <li ref={ref} style={{
      top: `${STACK_TOP_PX + index * STACK_PEEK_PX}px`
    }} className="sticky mb-4 rounded-[1.5rem] border border-shell/20 bg-sage px-5 pb-5 shadow-[0_-16px_32px_-20px_rgba(36,31,28,0.5)] lg:static lg:mb-0 lg:flex lg:min-h-[62svh] lg:flex-col lg:justify-center lg:rounded-none lg:border-0 lg:border-t lg:border-shell/25 lg:px-0 lg:py-16 lg:shadow-none" data-id="element-7540" data-mp="2d6hmq4eix8rj">
      {/* De volgorde is de boodschap; de lijst is al genummerd voor schermlezers. Op de computer is de actieve stap helder. */}
      <div className={`flex h-14 items-center gap-4 transition-opacity duration-200 ease-soft lg:block lg:h-auto ${isActive ? '' : 'lg:opacity-[0.65]'}`} data-id="element-7541" data-mp="3m4pr9f8fxw8j">
        <span aria-hidden="true" className="font-display text-[2rem] font-light leading-none text-blush lg:text-[6rem]" data-id="element-7542" data-mp="hpgkd3fvdgui">
          {index + 1}
        </span>
        <h3 className="font-display text-[1.6rem] font-light leading-none lg:mt-4 lg:text-[2.75rem]" data-id="element-7543" data-mp="3xjw5ahbnr3e">{title}</h3>
      </div>
      <p className="mt-2 max-w-md text-[16px] leading-relaxed text-shell lg:mt-5 lg:text-[17px]" data-id="element-7544" data-mp="8v1te3lz8hvk">{body}</p>
      {/* Telefoon: de foto van deze stap, onder de tekst (eerst lezen, dan kijken). */}
      <MediaView media={media} sizes="100vw" className="mt-5 aspect-[16/9] w-full overflow-hidden rounded-xl lg:hidden" data-id="element-7545" data-mp="11fjxgrsopey3" />
    </li>
  );
}